import { ActionIcon, Group, NumberInput, Popover, Slider, Tooltip, UnstyledButton } from "@mantine/core";
import { RefreshCw } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { SettingRange } from "../../../api/generated/model";
import classes from "./tools.module.css";

/**
 * A number box's change, as live takes it (`popover-slider-input`): empty clears the value; a
 * half-typed "." or "-" is not a number yet and changes nothing.
 */
export function onTyped(apply: (value: number | null) => void) {
  return (next: string | number) => {
    if (next === "") apply(null);
    else if (Number.isFinite(Number(next))) apply(Number(next));
  };
}

export interface RangeChipProps {
  /** Live's footer word: "Upscale", "Noise". */
  label: string;
  icon: ReactNode;
  /** Live's tooltip on the chip. */
  tip: string;
  range: SettingRange;
  /** The value, or null where none is set (the slider then sits at the default or the low end). */
  value: number | null;
  onChange: (value: number | null) => void;
  /** Live's decimals in the number box. */
  decimals: number;
}

/**
 * Live's popover slider (`popover-slider-input`, capture 08b): the chip with its icon and word,
 * live's tooltip, and above it a slider with its two ends marked, the number box and a reset to
 * the default.
 */
export function RangeChip({ label, icon, tip, range, value, onChange, decimals }: RangeChipProps) {
  const [opened, setOpened] = useState(false);
  const shown = value ?? range.default ?? range.min;
  return (
    <Popover opened={opened} onChange={setOpened} position="top-start" shadow="md" withinPortal={false}>
      <Tooltip label={tip} withinPortal={false} disabled={opened} multiline maw={300}>
        <Popover.Target>
          <UnstyledButton
            className={classes.chip}
            data-open={opened || undefined}
            aria-label={label}
            onClick={() => setOpened((open) => !open)}
          >
            {icon}
            <span>{label}</span>
          </UnstyledButton>
        </Popover.Target>
      </Tooltip>
      <Popover.Dropdown aria-label={label}>
        <Group gap="sm" wrap="nowrap">
          <Slider
            className={classes.slider}
            min={range.min}
            max={range.max}
            step={range.step}
            value={shown}
            label={null}
            aria-label={label}
            marks={[
              { value: range.min, label: String(range.min) },
              { value: range.max, label: String(range.max) },
            ]}
            onChange={onChange}
          />
          <NumberInput
            className={classes.number}
            aria-label={`${label} value`}
            min={range.min}
            max={range.max}
            step={range.step}
            decimalScale={decimals}
            clampBehavior="strict"
            hideControls
            value={value ?? ""}
            onChange={onTyped(onChange)}
          />
          <ActionIcon
            variant="subtle"
            aria-label={`Reset ${label}`}
            onClick={() => onChange(range.default ?? null)}
          >
            <RefreshCw size={16} />
          </ActionIcon>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}
